import React, { useEffect, useState } from 'react';
import {
  useStripeStatus,
  useStripeOnboard,
  useStripeDashboard,
} from '@theraply/api-client';

/**
 * Payments Settings Page
 * Displays Stripe Connect status and onboarding options.
 *
 * Features:
 * - Show connection status
 * - Connect with Stripe button
 * - Access to Stripe dashboard
 * - Payout status indicators
 * - Warning messages for incomplete onboarding
 */
export default function PaymentsPage() {
  const { getStatus, status, loading: statusLoading } = useStripeStatus();
  const { onboard, loading: onboardLoading } = useStripeOnboard();
  const { getDashboard, loading: dashboardLoading } = useStripeDashboard();

  const [showSuccess, setShowSuccess] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);

  useEffect(() => {
    // Load status on mount
    loadStatus();

    // Check URL parameters for success/refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setShowSuccess(true);
      // Refresh status after successful onboarding
      setTimeout(() => loadStatus(), 1000);
    }
    if (params.get('refresh') === 'true') {
      setShowRefresh(true);
    }
  }, []);

  const loadStatus = async () => {
    try {
      await getStatus();
    } catch (error) {
      console.error('Failed to load Stripe status:', error);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await onboard();
      // Open Stripe onboarding in new tab
      window.open(response.url, '_blank');
    } catch (error) {
      console.error('Failed to start onboarding:', error);
      alert('Failed to connect with Stripe. Please try again.');
    }
  };

  const handleOpenDashboard = async () => {
    try {
      const url = await getDashboard();
      // Open Stripe dashboard in new tab
      window.open(url, '_blank');
    } catch (error) {
      console.error('Failed to open dashboard:', error);
      alert('Failed to open Stripe dashboard. Please try again.');
    }
  };

  const isLoading = statusLoading || onboardLoading || dashboardLoading;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Payment Settings
        </h1>
        <p className="text-gray-600">
          Manage your payout settings and connect with Stripe to receive payments.
        </p>
      </div>

      {/* Success Message */}
      {showSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Successfully connected with Stripe
              </h3>
              <p className="mt-1 text-sm text-green-700">
                Your account is being reviewed. You'll be able to receive payouts once the review is complete.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Message */}
      {showRefresh && !status?.connected && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Onboarding incomplete
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                You need to complete the Stripe onboarding process to receive payouts.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Stripe Connect
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Connect your Stripe account to receive payouts from therapy sessions.
          </p>
        </div>

        <div className="px-6 py-5">
          {isLoading && !status ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : status?.connected ? (
            <div className="space-y-6">
              {/* Account Status */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Account Status
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <StatusItem
                    label="Account Connected"
                    status={true}
                  />
                  <StatusItem
                    label="Charges Enabled"
                    status={status.chargesEnabled || false}
                  />
                  <StatusItem
                    label="Payouts Enabled"
                    status={status.payoutsEnabled || false}
                  />
                  <StatusItem
                    label="Details Submitted"
                    status={status.detailsSubmitted || false}
                  />
                </div>
              </div>

              {/* Warning if not fully onboarded */}
              {!status.fullyOnboarded && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-amber-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-amber-800">
                        Complete your setup
                      </h3>
                      <p className="mt-1 text-sm text-amber-700">
                        Your account setup is incomplete. You won't be able to receive payouts until you complete all required information.
                      </p>
                      <div className="mt-3">
                        <button
                          onClick={handleConnect}
                          disabled={isLoading}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                        >
                          Complete Setup
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleOpenDashboard}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                  Open Stripe Dashboard
                </button>

                <button
                  onClick={loadStatus}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh Status
                </button>
              </div>

              {/* Account ID */}
              <div className="text-xs text-gray-500">
                Account ID: {status.accountId}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No Stripe account connected
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Connect with Stripe to start receiving payouts from therapy sessions.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleConnect}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg
                    className="mr-2 h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Connect with Stripe
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Information Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          About Stripe Connect
        </h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Secure payment processing through Stripe</li>
          <li>Automatic payouts to your bank account</li>
          <li>Real-time transaction monitoring</li>
          <li>PCI-compliant payment handling</li>
          <li>24/7 fraud protection</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * Status item component for displaying boolean status
 */
function StatusItem({ label, status }: { label: string; status: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-700">{label}</span>
      <span className="inline-flex items-center">
        {status ? (
          <>
            <svg
              className="h-5 w-5 text-green-500 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-medium text-green-700">Enabled</span>
          </>
        ) : (
          <>
            <svg
              className="h-5 w-5 text-gray-400 mr-1"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm font-medium text-gray-500">Disabled</span>
          </>
        )}
      </span>
    </div>
  );
}
